<template>
  <PageWrapper
    v-if="!isLoading"
    :include-footer-gap="false"
  >
    <div class="flex flex-col relative mt-n5 lg:mt-0 pt-5 lg:pt-0">
      <!-- White background for mobile -->
      <div class="absolute top-0 left-0 w-100 h-100 lg:hidden bg-white" />
      <ProductSuccessorProduct
        v-if="product.productSuccessor"
        :product="product.productSuccessor"
      />
      <ProductHero
        :product="product"
        :product-variants="productVariants"
        :is-hidden-product="isHiddenProduct"
        :active-tab="activeTab"
        :comments-count="allComments.length"
        @change-active-tab="changeActiveTab"
      />
      <ProductTabControl
        v-if="showProductTabControl"
        :product="product"
        :active-tab="activeTab"
        :comments-sum="product.commentsSum"
        :top-comments="firstTopComment"
        :comments="allComments"
        @change-active-tab="changeActiveTab"
      />
      <div
        v-if="showProductTabControl && product.healthBenefits"
        class="container mx-auto pb-7 lg:pl-0 pl-4 lg:pr-0 pr-4"
      >
        <div class="rounded-lg shadow bg-primary-100 px-4 py-4">
          <div class="flex items-center mb-3">
            <DidYouKnowSvg />
            <h3 class="my-0 ml-2">
              {{ t('did_you_know') }}
            </h3>
          </div>
          <p
            class="m-0 text-sm"
            v-html="product.healthBenefits"
          />
        </div>
      </div>
      <div
        v-if="showPreparation && product.videoUrl"
        class="mt-2 md:mt-8 md:mb-7 pt-2 md:pt-6 bg-primary-100"
      >
        <Wave
          path="waveOne"
          color="text-primary-100"
          class="relative mt-n5 hidden lg:block"
        />
        <div class="container">
          <div class="flex justify-center">
            <div class="lg:col-10 col-12 mx-0 px-0 mb-2">
              <!-- Universal best combination component -->
              <ProductPreparation
                class="col-12 mt-5"
                :product="product"
              />
            </div>
          </div>
        </div>
        <Wave
          path="waveOne"
          color="text-primary-200"
          class="relative mt-n5 hidden lg:block"
        />
      </div>
      <div class="container p-2">
        <div class="flex justify-center">
          <div class="lg:col-10 col-12 mx-0 px-0">
            <template
              v-for="(componentItem, index) in product.images.filter((item) => item.position !== null)"
              :key="index"
            >
              <ProductComponent
                class="px-1 py-4 md:py-6 lg:py-6 order-0"
                :item="componentItem"
                :item-position="componentItem.position"
              />
            </template>
            <div
              v-if="showNews"
              class="flex flex-col"
            >
              <header class="mb-5 text-center block zindex-2">
                <h2 class="mb-3 md:mb-4 lg:mb-5 text-center block">
                  {{ t('comment_moment') }}
                </h2>
              </header>
              <div class="px-3">
                <ProductReviews
                  :product="product"
                  :active-tab="activeTab"
                  :top-comments="firstTopComment"
                  :comments="allComments"
                />
              </div>
            </div>
            <ProductComponent
              v-if="showNews"
              class="px-1 py-4 md:py-6 lg:py-6 order-0"
              :item="news"
              item-position="bottom"
            />
          </div>
        </div>
      </div>
      <!-- Show only when needed -->
      <CrossSell
        v-if="crossSell"
        :cross-sell-products="crossSell"
        class="order-10"
        :show-negative-top-wave="true"
        slider-id="recommend-to-buy"
      />
      <ProductSimilarProducts
        v-if="similarProducts"
        :similar-products="similarProducts"
      />
    </div>
    <div class="add-to-cart-fixed bg-white fixed bottom-0 left-0 right-0 p-2 lg:hidden flex">
      <div
        v-if="product.isReadyProduct || product.isGift"
        class="col lg:col-auto"
      >
        <AddToCartButton
          v-if="product.state === 'available'"
          :show-decrement-button="true"
          :product="product"
          :wide="true"
          theme="transparent"
          :odometer-fill="true"
        />
        <ButtonState
          v-else
          :state="product.state"
          :product="product"
          added-class="w-100"
        />
      </div>
      <div
        v-else-if="product.isIngredient"
        class="col lg:col-auto pt-2"
      >
        <PushToMixButton
          v-if="product.state === 'available'"
          :product="product"
          added-class="w-100"
        />
        <ButtonState
          v-else
          :state="product.state"
          :product="product"
          added-class="w-100"
        />
      </div>
    </div>
  </PageWrapper>
  <BaseModal
    :title="t('copy_link')"
    modal-id="share-product"
  >
    <ModalProductShare />
  </BaseModal>
  <BaseModal
    :title="t('watchdog_title')"
    modal-id="modal-watchdog"
  >
    <ModalWatchdog />
  </BaseModal>
  <BaseModal modal-id="product-detail-modal">
    <ModalProductDetail />
  </BaseModal>
  <BaseModal
    :title="t('choose_type_of_mix_title')"
    modal-id="push-to-mix-modal"
  >
    <ModalPushToMix />
  </BaseModal>
  <BaseModal
    modal-id="modal-fullscreen-product-image"
    background-color="bg-white"
    wave-color="text-white"
  >
    <ModalFullscreenImage />
  </BaseModal>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import BaseModal from '@/components/general/BaseModal.vue'
import PageWrapper from '@/components/general/BasePageWrapper.vue'

import ProductHero from '@/components/products/ProductHero.vue'
import ProductSuccessorProduct from '@/components/products/ProductSuccessorProduct.vue'
import { ensureNoDoubleSlash } from '@/mixins/useEnsure'
import { GtmEventEmitter } from '@/utils/gtmEventEmitter'
import { useAppStore } from '@/stores/AppStore'
import { useNavbarStore } from '@/stores/NavbarStore'
import { useProductStore } from '@/stores/ProductStore'

const ProductComponent = defineAsyncComponent(() => import('@/components/products/ProductComponent.vue'))
const ProductPreparation = defineAsyncComponent(() => import('@/components/products/ProductPreparation.vue'))
const CrossSell = defineAsyncComponent(() => import('@/components/general/BaseCrossSell.vue'))
const ProductSimilarProducts = defineAsyncComponent(() => import('@/components/products/ProductSimilarProducts.vue'))
const ProductReviews = defineAsyncComponent(() => import('@/components/products/ProductReviews.vue'))

const Wave = defineAsyncComponent(() => import('@/components/general/BaseWave.vue'))
const ModalFullscreenImage = defineAsyncComponent(() => import('@/components/modals/ModalFullscreenImage.vue'))
const ModalProductDetail = defineAsyncComponent(() => import('@/components/modals/ModalProductDetail.vue'))
const ModalProductShare = defineAsyncComponent(() => import('@/components/modals/ModalProductShare.vue'))
const ModalPushToMix = defineAsyncComponent(() => import('@/components/modals/ModalPushToMix.vue'))
const ModalWatchdog = defineAsyncComponent(() => import('@/components/modals/ModalWatchdog.vue'))
const ProductTabControl = defineAsyncComponent(() => import('@/components/products/ProductTabControl.vue'))
const DidYouKnowSvg = defineAsyncComponent(() => import('@/components/svg/DidYouKnowSvg.vue'))

const AddToCartButton = defineAsyncComponent(() => import('@/components/buttons/ButtonAddToCartButton.vue'))
const PushToMixButton = defineAsyncComponent(() => import('@/components/buttons/ButtonPushToMixButton.vue'))
const ButtonState = defineAsyncComponent(() => import('@/components/buttons/ButtonState.vue'))

const route = useRoute()
const urlPath = route.fullPath
const permalink = ref(route.params.permalink)
const { t } = useI18n()

const { setNavbar } = useNavbarStore()
const { isFavorite } = useAppStore()
const productStore = useProductStore()

const appStore = useAppStore()
const { getCurrentCustomer, isSignedIn } = storeToRefs(appStore)

setNavbar({
  backgroundColor: 'bg-primary-700',
})

const isLoading = ref(true)
const product = ref()
const productVariants = ref()
const crossSell = ref(null)
const similarProducts = ref(null)
const showProductTabControl = ref(false)
const showPreparation = ref(false)
const showCustom = ref(false)
const showNews = ref(false)
const news = ref(null)
const comments = ref(null)

const loadComments = async () => {
  const { data } = await ApiServiceV1.get(`products/${product.value.id}/product_comments?limit=all`)
  comments.value = data
}

const loadNews = async () => {
  const { data } = await RetailApiV2.get('news?limit=1&kind=product_detail')
  news.value = data.news[0]
}


const loadProduct = async () => {
  try {
    const { data } = await ApiServiceV1.get(`products/${permalink.value}?limit=10`)
    product.value = data.product
    productVariants.value = data.productVariants
    loadNews()

    isLoading.value = false

    showSectionsHandler()

    // Sends GTM event page_view
    GtmEventEmitter.pageView({
      type: 'product',
      title: data.product.name,
      url: ensureNoDoubleSlash(`${import.meta.env.VITE_VUE_APP_ORIGINAL_URL}/${urlPath}`),
      categories: data.product.categories.map((c) => {
        return c.title
      }),
    })

    // Sends GTM event view_item
    GtmEventEmitter.viewItem({
      product: data.product,
      isFavorite: isFavorite(data.product.id),
    })
  } catch {
    window.location = '/404'
  }
}
loadProduct()

const firstTopComment = computed(() => {
  if (!comments.value) return []

  const all = [...(comments.value.comments || []), ...(comments.value.variantsComments || [])]

  return all.length > 0 && all[0].top ? [all[0]] : []
})

const allComments = computed(() => {
  if (!comments.value) return []

  const all = [...(comments.value.comments || []), ...(comments.value.variantsComments || [])]

  return all.length > 0 && all[0].top ? all.slice(1) : all
})

const activeTab = ref(1)

const changeActiveTab = (id) => {
  activeTab.value = id
  if (id === 3 && !firstTopComment.value.lenght && !allComments.value.lenght) {
    loadComments()
  }
}

const isHiddenProduct = computed(() => {
  return product.value.state === 'removed' || product.value.state === 'hidden'
})

const tabControl = 0
const preparationStep = 1
const customStep = 2
const newsStep = 3
const crossSellStep = 4
const similarProductsStep = 5
const allLoaded = 6

const dataIsLoading = ref(false)
const loadingStep = ref(tabControl)

const showSectionsHandler = async () => {
  const footer = document.querySelector('#footer-wrapper')
  if (
    footer instanceof HTMLElement &&
    footer.getBoundingClientRect().top < window.innerHeight + 400 &&
    !dataIsLoading.value &&
    !isLoading.value
  ) {
    dataIsLoading.value = true
    switch (loadingStep.value) {
      case tabControl: {
        showProductTabControl.value = true
        loadingStep.value = preparationStep
        break
      }
      case preparationStep: {
        showPreparation.value = true
        loadingStep.value = customStep
        break
      }
      case customStep: {
        showCustom.value = true
        loadingStep.value = newsStep
        break
      }
      case newsStep: {
        showNews.value = true
        if (!firstTopComment.value.lenght && !allComments.value.lenght) {
          loadComments()
        }
        loadingStep.value = crossSellStep
        break
      }
      case crossSellStep: {
        crossSell.value = await productStore.loadCrossSell('quantity=6&kind=product_detail_cross_sell')
        loadingStep.value = similarProductsStep
        break
      }
      case similarProductsStep: {
        similarProducts.value = (
          await ApiServiceV1.get(`products/${product.value.id}/similar_products`)
        ).data?.similarProducts
        loadingStep.value = allLoaded
        break
      }
      case allLoaded: {
        break
      }
      default: {
        break
      }
    }
    dataIsLoading.value = false
  }
}

const showAddToCart = () => {
  const staticAddToCartBtn = document.querySelector('.product-buttons')
  const addToCartBtn = document.querySelector('.add-to-cart-fixed')
  if (addToCartBtn && staticAddToCartBtn) {
    if (staticAddToCartBtn.getBoundingClientRect().top < 0) addToCartBtn.classList.add('active')
    else addToCartBtn.classList.remove('active')
  }
}

window.addEventListener('scroll', async () => {
  await showSectionsHandler()
  showAddToCart()
})

const hashedWord = ref('comments')

const scrollToTabAndActivate = () => {
  setTimeout(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: document.querySelector('#tabControl').offsetTop + document.querySelector('#main').offsetTop - 20,
    })
    changeActiveTab(3)
  }, 500)
}

watchEffect(() => {
  const hashIndex = urlPath.indexOf('#')
  if (hashIndex !== -1) {
    const currentHashedWord = urlPath.slice(Math.max(0, hashIndex + 1))
    if (currentHashedWord === hashedWord.value) {
      scrollToTabAndActivate()
    }
  }
})

onBeforeRouteUpdate((to, from, next) => {
  permalink.value = to.params.permalink
  isLoading.value = true
  showNews.value = false
  loadingStep.value = tabControl
  loadProduct()
  next()
})
</script>

<style scoped lang="scss">
.add-to-cart-fixed {
  z-index: 100;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  transform: translateY(100%);
  transition: transform 0.3s ease-in-out;

  &.active {
    transform: translateY(0%);
  }
}
</style>

<i18n>
  {
    "cs": {
    "benefits": "Výhody",
    "best_combination_description": "Vyznejte lásku zamilovanými dárkovými balíčky. Namixovali jsme vášnivý mix Elixír z lásky, celé mrazem sušené jahůdky, čokoládové kousky s malinami a mandlemi nebo zbrusu nové Crème boule - Love Berry.",
    "best_combination_link": "https://www.mixit.cz/hotove-mixit/valentyn",
    "best_combination_title": "Křupavý Valentýn",
    "choose_type_of_mix_title": "Zvolte, jaký typ mixu chcete míchat:",
    "comment_moment": "Moment, ještě koment",
    "copy_link": "Zkopírujte si odkaz",
    "did_you_know": "Víte, že?",
    "evaluation": "Hodnocení",
    "ingredients": "Složení",
    "nutrition_information": "Výživové údaje",
    "video": "Video",
    "watchdog_title": "Chcete si počkat?",
    "what_a_good_buy": "Doporučujeme přikoupit"
},
    "sk": {
    "benefits": "Výhody",
    "best_combination_description": "Vyznajte lásku zamilovanými darčekovými balíčkami. Namixovali sme vášnivý mix Elixír z lásky, celé mrazom sušené jahôdky, čokoládové kúsky s malinami a mandľami i úplne nové Crème boule - Love Berry.",
    "best_combination_link": "https://www.mixit.sk/hotove-mixit/valentin",
    "best_combination_title": "Chrumkavý Valentín",
    "choose_type_of_mix_title": "Zvoľte, aký typ mixu chcete miešať:",
    "comment_moment": "Moment, ešte koment",
    "copy_link": "Skopírujte si odkaz",
    "did_you_know": "Viete, že?",
    "evaluation": "Hodnotenie",
    "ingredients": "Zloženie",
    "nutrition_information": "Výživové údaje",
    "video": "Video",
    "watchdog_title": "Chcete si počkať?",
    "what_a_good_buy": "Čo dobré prikúpiť"
},
    "pl": {
    "benefits": "Zalety",
    "best_combination_description": "Wyraź swoją miłość z zakochanymi zestawami podarunkowymi od Mixit! Oprócz nich na prezent idealnie sprawdzą się: pyszne, limitowane musli Eliksir miłości, całe liofilizowane truskawki w uroczym cukierku, czekoladowe grudki z malinami i migdałami czy zupełnie nowe Crème boule — Love Berry.",
    "best_combination_link": "https://www.mixit.pl/gotowe-mixit/walentynki",
    "best_combination_title": "Chrupiące Walentynki",
    "choose_type_of_mix_title": "Wybierz, jaki typ mixu chcesz namixować:",
    "comment_moment": "Smakowało? Zostaw swój komentarz.",
    "copy_link": "Skopiuj link",
    "did_you_know": "Czy wiesz, że?",
    "evaluation": "Recenzje",
    "ingredients": "Skład",
    "nutrition_information": "Wartości odżywcze",
    "video": "Video",
    "watchdog_title": "Chcesz poczekać?",
    "what_a_good_buy": "Warto dodać"
},
    "hu": {
    "benefits": "Előnyök",
    "best_combination_description": "Fejezd ki szeretetedet szerelmes ajándékcsomagokkal. Összeállítottunk egy mixet szenvedélyes Szerelmi elixírből , egész liofilizált eperből, csokoládédarabokból málnával és mandulával vagy a vadonatúj Creme boule - Love Berry-ből.",
    "best_combination_link": "https://www.mixit.hu/kesztermekek/valentin",
    "best_combination_title": "Ropogós Valentin-nap",
    "choose_type_of_mix_title": "Válaszd ki milyen fajta mixet szeretnél keverni!",
    "comment_moment": "Várj! Dobj egy értékelést.",
    "copy_link": "Link másolás",
    "did_you_know": "Tudtad?",
    "evaluation": "Értékelések",
    "ingredients": "Összetevők",
    "nutrition_information": "Tápértékadatok",
    "video": "Videó",
    "watchdog_title": "Szeretnél várni?",
    "what_a_good_buy": "Mi finomat rendeljek még?"
},
    "de": {
    "benefits": "Vorteile",
    "best_combination_description": "Bringe deine Liebe mit Liebesgeschenkpaketen zum Ausdruck. Wir haben eine leidenschaftliche Mischung aus Love Elixir, ganzen gefriergetrockneten Erdbeeren, Schokoladenstückchen mit Himbeeren und Mandeln oder der brandneuen Crème boule - Love Berry zusammengestellt.",
    "best_combination_link": "https://www.mixit.de/fertig-mixit/valentinstag",
    "best_combination_title": "Knuspriger Valentinstag",
    "choose_type_of_mix_title": "Wähle, welche Art von Mix du zusammenstellen möchtest.",
    "comment_moment": "Warte, hinterlasse noch einen Kommentar",
    "copy_link": "Link kopieren",
    "did_you_know": "Wusstest du?",
    "evaluation": "Bewertung",
    "ingredients": "Zutaten",
    "nutrition_information": "Nährwertangaben",
    "video": "Video",
    "watchdog_title": "Möchtest du warten?",
    "what_a_good_buy": "Wir empfehlen, folgendes hinzuzufügen"
},
    "at": {
    "benefits": "Vorteile",
    "best_combination_description": "Bringe deine Liebe mit Liebesgeschenkpaketen zum Ausdruck. Wir haben eine leidenschaftliche Mischung aus Love Elixir, ganzen gefriergetrockneten Erdbeeren, Schokoladenstückchen mit Himbeeren und Mandeln oder der brandneuen Crème boule - Love Berry zusammengestellt.",
    "best_combination_link": "https://www.mixit.de/fertig-mixit/valentinstag",
    "best_combination_title": "Knuspriger Valentinstag",
    "choose_type_of_mix_title": "Wähle, welche Art von Mix du zusammenstellen möchtest.",
    "comment_moment": "Warte, hinterlasse noch einen Kommentar",
    "copy_link": "Link kopieren",
    "did_you_know": "Wusstest du?",
    "evaluation": "Bewertung",
    "ingredients": "Zutaten",
    "nutrition_information": "Nährwertangaben",
    "video": "Video",
    "watchdog_title": "Möchtest du warten?",
    "what_a_good_buy": "Wir empfehlen, folgendes hinzuzufügen"
},
    "ro": {
    "benefits": "Beneficii",
    "best_combination_description": "Mărturisește dragostea cu pachete cadou. Am amestecat un mix pasional Elixirul dragostei, căpșune întregi liofilizate, bucăți de ciocolată cu zmeură și migdale sau noutatea  Biluțe cremoase - Love Berry.",
    "best_combination_link": "https://www.mixit.ro/mixit-pregatit/valentine",
    "best_combination_title": "Ziua Îndrăgostiților crocantă",
    "choose_type_of_mix_title": "Alegeti tipul mixului pentru amestecul dumneavoastra:",
    "comment_moment": "Un moment, adăugați o recenzie",
    "copy_link": "Copiază linkul",
    "did_you_know": "Știați că?",
    "evaluation": "Opinii",
    "ingredients": "Ingrediente",
    "nutrition_information": "Informații nutriționale",
    "video": "Videoclip",
    "watchdog_title": "Dorești să aștepți?",
    "what_a_good_buy": "Ce bunătate să mai adaug"
}
  }
</i18n>
